import {Three as THREE, ThreeTsl as TSL} from "#three-texture-streaming/three-exports"
import type {ComputeMipLevelTsl} from "#three-texture-streaming/shader-helpers"
import {splitMipLevelTsl, srgbToLinearTsl} from "#three-texture-streaming/shader-helpers"
import type {AtlasFormat} from "#three-texture-streaming/texture-tile-constants"
import {ALLOW_TILE_DEBUG_RENDER_MODES, TEXTURE_TILE_MARGIN, TEXTURE_TILE_SIZE} from "#three-texture-streaming/texture-tile-constants"
import {assertNever} from "@cm/utils"
import type {TextureNodeLike} from "#three-texture-streaming/texture-node-like"
import type {Node} from "three/webgpu"
import {float} from "three/tsl"

const TILE_WITH_MARGIN = TEXTURE_TILE_SIZE + TEXTURE_TILE_MARGIN * 2

type FloatNode = THREE.Node<"float">
type IntNode = THREE.Node<"int">
type Vec2Node = THREE.Node<"vec2">
type Vec3Node = THREE.Node<"vec3">
type Vec4Node = THREE.Node<"vec4">

interface AtlasUvNodes {
    atlasUv: Vec2Node
    layer: FloatNode
    effectiveMipFrac: FloatNode
    atlasSize: Vec2Node
    mipLevelScale: FloatNode // 1 / 2^mipLevelInt
    mipShiftScale: FloatNode // 1 / 2^mipShiftInt
    mipLevelInt: IntNode
    tileLookupUv: Vec2Node
    tileIndexF: Vec2Node
    intraTileUv: Vec2Node
    atlasTileInfoF: Vec4Node
    uvFlipped: Vec2Node
}

// !!! CAUTION !!! In the following function, adding toVar() to variables can seriously break things (due to TSL code generation bugs) ! When making changes, always check
// that mipmap computation and blending still works correctly. Also, don't be fooled by it looking correct at first and when zooming in, as this can just mean that the correct
// tiles are requested so you see the correct tiles, while this function is broken and always requests the most detailed level available. So make sure zooming back out reverts
// to using coarser tiles.
function buildAtlasUvNodes(
    atlasTextureNode: THREE.TextureNode,
    tileLookup: THREE.Texture,
    textureSize: THREE.Vector2,
    uvIn: Vec2Node | Vec3Node,
    computeMipLevelTsl: ComputeMipLevelTsl,
    minMipLevel: THREE.UniformNode<"float", number>,
    maxPixelsPerTexel: number,
): AtlasUvNodes {
    const tileLookupImage = tileLookup.image as {width: number; height: number}

    const tileLookupSize = TSL.uniform(new THREE.Vector2(tileLookupImage.width, tileLookupImage.height))
    const tileLookupMipLevels = TSL.uniform(tileLookup.mipmaps.length > 0 ? tileLookup.mipmaps.length : 1)
    const textureSizeUniform = TSL.uniform(textureSize)

    // Flip Y: tile data has origin at top-left, UV origin is bottom-left
    const uvFlipped = TSL.vec2(uvIn.x, TSL.float(1).sub(uvIn.y))

    // Derivatives of UV in texel space
    const uvFlippedScaled = uvFlipped.mul(textureSizeUniform)
    const ddx = TSL.dFdx(uvFlippedScaled) //.toVar() // TODO these toVar() should be good to fix the dFdx computation here to be evaluated unconditionally, but for some strange reason it will cause the dFdx to evaluate wrongly
    const ddy = TSL.dFdy(uvFlippedScaled) //.toVar()

    const maxMipLevel = tileLookupMipLevels.sub(TSL.float(1)).max(TSL.float(0))
    const maxPixelsPerTexelNode = TSL.float(maxPixelsPerTexel)
    const mipLevelContinuous = computeMipLevelTsl(ddx, ddy, minMipLevel, maxMipLevel, maxPixelsPerTexelNode)
    const {mipLevelInt, mipLevelFrac} = splitMipLevelTsl(mipLevelContinuous, maxMipLevel)

    // mipLevelScale = 1 / 2^mipLevelInt
    const mipLevelScale = TSL.reciprocal(TSL.shiftLeft(TSL.int(1), mipLevelInt).toFloat())

    const tileSize = TSL.float(TEXTURE_TILE_SIZE)
    const numTiles = textureSizeUniform.div(tileSize)

    const tileIndexF = TSL.fract(uvFlipped).mul(numTiles).mul(mipLevelScale)
    const tileIndex = TSL.floor(tileIndexF)

    // Look up which atlas tile covers this virtual tile at this mip level
    const tileLookupUv = tileIndex.add(TSL.float(0.5)).div(tileLookupSize.mul(mipLevelScale))
    const tileInfoF = TSL.texture(tileLookup, tileLookupUv).level(mipLevelInt.toFloat()) as Vec4Node

    // Decode: tile lookup stores tile info packed as 8-bit values in RGBA, read back as [0,1] floats
    const atlasTileInfoF = TSL.round(tileInfoF.mul(TSL.float(255)) as unknown as FloatNode) as unknown as Vec4Node // round(vec4) is currently not in the types

    // atlasTileIndex.xy * tileWithMargin + margin gives the texel origin in the atlas
    const tileOffset = atlasTileInfoF.xy.mul(TSL.float(TILE_WITH_MARGIN)).add(TSL.float(TEXTURE_TILE_MARGIN))

    // mipShift: the atlas tile may be from a coarser mip level
    const mipShiftInt = TSL.int(atlasTileInfoF.w)
    const mipShiftScale = TSL.reciprocal(TSL.shiftLeft(TSL.int(1), mipShiftInt).toFloat())
    const intraTileUv = TSL.fract(tileIndexF.mul(mipShiftScale))
    const tileTexel = intraTileUv.mul(TSL.float(TEXTURE_TILE_SIZE))

    // layer (z channel) is the DataArrayTexture layer index
    const layer = atlasTileInfoF.z

    // Atlas dimensions from the DataArrayTexture image
    const atlasSize = atlasTextureNode.size(TSL.int(0)) as Vec2Node

    const atlasUv = tileOffset.add(tileTexel).div(atlasSize)

    // When using a fallback tile (mipShift > 0), blending toward the fallback's own coarser
    // atlas slot (mip1) would show content even coarser than the fallback itself. Clamp to 0
    // so only the fallback's own content (atlas mip0) is sampled.
    const mipShift = atlasTileInfoF.w
    const effectiveMipFrac = TSL.select(mipShift.greaterThan(TSL.float(0)), TSL.float(0), mipLevelFrac)

    return {
        atlasUv,
        layer,
        effectiveMipFrac,
        atlasSize,
        mipLevelScale,
        mipShiftScale,
        mipLevelInt,
        tileLookupUv,
        tileIndexF,
        intraTileUv,
        atlasTileInfoF,
        uvFlipped,
    }
}

function readFromAtlas(args: {
    atlasFormat: AtlasFormat
    atlasTextureNode: THREE.TextureNode
    applySrgbTransform: boolean
    atlasUv: Vec2Node
    layer: FloatNode
    effectiveMipFrac: FloatNode
}): Node<"vec4"> {
    const rawSample = TSL.texture(args.atlasTextureNode, args.atlasUv).depth(args.layer).level(args.effectiveMipFrac) as Vec4Node
    const toLinear = (ch: FloatNode): FloatNode => (args.applySrgbTransform ? srgbToLinearTsl(ch) : ch)
    switch (args.atlasFormat) {
        case "rgba8": {
            return TSL.vec4(toLinear(rawSample.r), toLinear(rawSample.g), toLinear(rawSample.b), rawSample.a)
        }
        case "r8": {
            // r8 atlas: WebGPU spec guarantees RedFormat returns (R, 0, 0, 1) — replicate R into RGB
            const r = toLinear(rawSample.r)
            return TSL.vec4(r, r, r, TSL.float(1))
        }
        default:
            assertNever(args.atlasFormat)
            throw new Error(`Unsupported atlas format ${args.atlasFormat}`)
    }
}

export class TextureVirtualThreeNode extends THREE.TempNode<"vec4"> implements TextureNodeLike {
    constructor(
        readonly atlasTextureNode: THREE.TextureNode,
        readonly tileLookup: THREE.Texture,
        readonly textureSize: THREE.Vector2,
        readonly uvNode: Vec2Node | Vec3Node,
        readonly applySrgbTransform: boolean,
        readonly atlasFormat: AtlasFormat,
        private computeMipLevelTsl: ComputeMipLevelTsl,
        minMipLevel: number,
        private readonly maxPixelsPerTexel: number,
        readonly tiledTextureId: number,
        private readonly onBeforeFrame: () => void,
        initialRenderMode: number,
    ) {
        super("vec4")
        this.updateBeforeType = THREE.NodeUpdateType.FRAME
        this.minMipLevel = TSL.uniform(minMipLevel)
        this.renderMode = TSL.uniform(initialRenderMode)
    }

    override updateBefore(_frame: THREE.NodeFrame): boolean | undefined {
        this.onBeforeFrame()
        return undefined
    }

    override generate(builder: THREE.NodeBuilder, output?: string | null): string | null | undefined {
        const type = this.getNodeType(builder)

        const {atlasUv, layer, effectiveMipFrac, mipLevelInt, tileLookupUv, tileIndexF, intraTileUv, atlasTileInfoF, uvFlipped} = buildAtlasUvNodes(
            this.atlasTextureNode,
            this.tileLookup,
            this.textureSize,
            this.uvNode,
            this.computeMipLevelTsl,
            this.minMipLevel,
            this.maxPixelsPerTexel,
        )

        const finalColor = readFromAtlas({
            atlasFormat: this.atlasFormat,
            atlasTextureNode: this.atlasTextureNode,
            applySrgbTransform: this.applySrgbTransform,
            atlasUv,
            layer,
            effectiveMipFrac,
        })

        let result: Node<"vec4">
        if (ALLOW_TILE_DEBUG_RENDER_MODES) {
            const tiledTextureIdUniform = TSL.uniform(this.tiledTextureId)
            const idR = TSL.float(TSL.bitAnd(tiledTextureIdUniform, TSL.int(0xf)).shiftLeft(4)).div(TSL.float(255))
            const idG = TSL.float(TSL.bitAnd(tiledTextureIdUniform, TSL.int(0xf0))).div(TSL.float(255))
            const idB = TSL.float(TSL.shiftRight(TSL.bitAnd(tiledTextureIdUniform, TSL.int(0xf00)), 4)).div(TSL.float(255))
            const idDebugColor = TSL.vec4(idR, idG, idB, TSL.float(1))

            const mipColors: Vec4Node[] = [
                TSL.vec4(1, 1, 1, 1), // 0: white
                TSL.vec4(1, 1, 0, 1), // 1: yellow
                TSL.vec4(0, 1, 1, 1), // 2: cyan
                TSL.vec4(1, 0, 1, 1), // 3: magenta
                TSL.vec4(0, 1, 0, 1), // 4: green
                TSL.vec4(1, 0, 0, 1), // 5: red
                TSL.vec4(0, 0, 1, 1), // 6: blue
            ]
            let mipDebugColor: THREE.Node = TSL.vec4(0, 0, 0, 1) // 7+: black
            for (let i = 6; i >= 0; i--) {
                mipDebugColor = TSL.select(mipLevelInt.equal(TSL.int(i)), mipColors[i], mipDebugColor)
            }

            const tileDebugColor = TSL.vec4(tileLookupUv, TSL.float(0), TSL.float(1))
            const intraTileUvDebugColor = TSL.vec4(intraTileUv, TSL.float(0), TSL.float(1))
            const mipShiftDebugColor = TSL.vec4(atlasTileInfoF.w.div(TSL.float(10)), TSL.float(0), TSL.float(0), TSL.float(1))
            const uvDebugColor = TSL.vec4(TSL.fract(uvFlipped), TSL.float(0), TSL.float(1))
            const tileIndexFDebugColor = TSL.vec4(TSL.fract(tileIndexF), TSL.float(0), TSL.float(1))
            const mipLevelFracDebugColor = TSL.vec4(effectiveMipFrac, effectiveMipFrac, effectiveMipFrac, TSL.float(1))

            result = TSL.select(
                this.renderMode.equal(TSL.int(1)),
                mipDebugColor,
                TSL.select(
                    this.renderMode.equal(TSL.int(2)),
                    tileDebugColor,
                    TSL.select(
                        this.renderMode.equal(TSL.int(3)),
                        idDebugColor,
                        TSL.select(
                            this.renderMode.equal(TSL.int(4)),
                            intraTileUvDebugColor,
                            TSL.select(
                                this.renderMode.equal(TSL.int(5)),
                                mipShiftDebugColor,
                                TSL.select(
                                    this.renderMode.equal(TSL.int(6)),
                                    uvDebugColor,
                                    TSL.select(
                                        this.renderMode.equal(TSL.int(7)),
                                        tileIndexFDebugColor,
                                        TSL.select(this.renderMode.equal(TSL.int(8)), mipLevelFracDebugColor, finalColor),
                                    ),
                                ),
                            ),
                        ),
                    ),
                ),
            ) as Vec4Node
        } else {
            result = finalColor
        }

        return result.build(builder, output ?? type) as string | null
    }

    // TextureNodeLike
    sample(uv: Vec2Node): Vec4Node {
        return new TextureVirtualThreeNode(
            this.atlasTextureNode,
            this.tileLookup,
            this.textureSize,
            uv,
            this.applySrgbTransform,
            this.atlasFormat,
            this.computeMipLevelTsl,
            this.minMipLevel.value,
            this.maxPixelsPerTexel,
            this.tiledTextureId,
            this.onBeforeFrame,
            this.renderMode.value,
        )
    }

    sampleGradient(uv: Vec2Node): {dFdU: Vec4Node; dFdV: Vec4Node} {
        const {atlasUv, layer, effectiveMipFrac, atlasSize, mipLevelScale, mipShiftScale} = buildAtlasUvNodes(
            this.atlasTextureNode,
            this.tileLookup,
            this.textureSize,
            uv,
            this.computeMipLevelTsl,
            this.minMipLevel,
            this.maxPixelsPerTexel,
        )

        // Step of 1 atlas pixel — always within the tile margin
        const atlasPixelStep = TSL.vec2(TSL.reciprocal(float(atlasSize.x)), TSL.reciprocal(float(atlasSize.y)))

        // Closure over shared atlas state; only offset and explicit mip level vary per call.
        const atlasSampler = (offset: Vec2Node, mipFrac: FloatNode): Vec4Node =>
            readFromAtlas({
                atlasFormat: this.atlasFormat,
                atlasTextureNode: this.atlasTextureNode,
                applySrgbTransform: this.applySrgbTransform,
                atlasUv: atlasUv.add(offset),
                layer,
                effectiveMipFrac: mipFrac,
            })

        // Centred finite-difference gradient at a given atlas mip level.
        // Both mip0 and mip1 are accessed via the level-N tile's atlasUv, so they share the
        // same Jacobian d(atlasUV)/d(virtualUV) and therefore the same normScale.
        // Atlas V = 1 - original V (Y-flip in buildAtlasUvNodes) → negate V gradient.
        const textureSizeNode = TSL.uniform(this.textureSize) as Vec2Node
        const finiteDiffGrad = (mipFrac: FloatNode, pixelStep: Vec2Node, normScale: FloatNode) => {
            const px = TSL.vec2(pixelStep.x, TSL.float(0))
            const mx = TSL.vec2(pixelStep.x.negate(), TSL.float(0))
            const py = TSL.vec2(TSL.float(0), pixelStep.y)
            const my = TSL.vec2(TSL.float(0), pixelStep.y.negate())
            return {
                dFdU: atlasSampler(px, mipFrac).sub(atlasSampler(mx, mipFrac)).mul(textureSizeNode.x).mul(normScale).mul(mipShiftScale).mul(TSL.float(0.5)),
                dFdV: atlasSampler(my, mipFrac).sub(atlasSampler(py, mipFrac)).mul(textureSizeNode.y).mul(normScale).mul(mipShiftScale).mul(TSL.float(0.5)),
            }
        }

        const grad0 = finiteDiffGrad(TSL.float(0), atlasPixelStep, mipLevelScale)
        const grad1 = finiteDiffGrad(TSL.float(1), atlasPixelStep.mul(2), mipLevelScale.mul(0.5))

        // Force outer node into the graph for updateBefore registration so tile table updates are flushed.
        const zeroRef = this.mul(TSL.float(0))

        return {
            dFdU: TSL.mix(grad0.dFdU, grad1.dFdU, effectiveMipFrac).add(zeroRef),
            dFdV: TSL.mix(grad0.dFdV, grad1.dFdV, effectiveMipFrac),
        }
    }

    minMipLevel: THREE.UniformNode<"float", number>
    renderMode: THREE.UniformNode<"float", number>
}
